import type { TioSchemaManifest } from 'tio-data/store-factory';

declare global {
  interface ImportMeta {
    glob(pattern: string, options: { eager: true }): Record<string, unknown>;
  }
}

type Schemas = NonNullable<TioSchemaManifest['schemas']>;

// Only modules with a default export register anything: an *.ext.ts that holds
// nothing but types (search.ext.ts) is skipped.
function defaultExports<T>(modules: Record<string, unknown>): T[] {
  return Object.values(modules).flatMap((module) => {
    const exported = (module as { default?: T }).default;
    return exported === undefined ? [] : [exported];
  });
}

/** Every resource schema under `data/resources`, in path order. */
export const schemas = defaultExports<Schemas[number]>(
  import.meta.glob('./data/resources/*.schema.ts', { eager: true })
);

/**
 * Every object extension: the resource `*.ext.ts` registrations plus the trait ones
 * under `data/traits`. A trait extension left out drops its getters from every record
 * carrying the trait, with no error at runtime.
 */
export const extensions = [
  ...defaultExports<unknown>(import.meta.glob('./data/resources/*.ext.ts', { eager: true })),
  ...defaultExports<unknown>(import.meta.glob('./data/traits/*.ext.ts', { eager: true })),
];

/** Every trait schema under `data/traits`. */
export const traits = defaultExports<unknown>(
  import.meta.glob('./data/traits/*.schema.ts', { eager: true })
);

/** Complete registration set: every schema, extension, and trait in tio-data. */
export const manifest: TioSchemaManifest = {
  schemas,
  traits,
  extensions,
};

export default manifest;
